// Read-only rendering of the jsonb shape get_week_plan_by_share_token /
// get_week_plan_for_household (and their day-level counterparts) return --
// factored out of SharedWeekPlanPage.jsx/SharedWeekPlanDayPage.jsx so the
// authenticated "Shared with us" page can show the exact same preview
// without duplicating the markup. Recipe titles only, by design -- see
// 0020_week_plan_share_links.sql's comment on why a plan link/share never
// carries full recipe content. Since slice 8b (0060) a recipe that has its
// own link turned on, and belongs to the plan's household, comes with that
// link (recipeLink), and its name opens it.
import { DAYS, MEAL_SLOTS, RECIPE_TYPE_LABELS, FONT_SIZE, SPACE, COLORS } from "../lib/constants";
import { formatWeekRange } from "../lib/utils";

// A recipe's name, linked to its own share link when the plan carries one.
function RecipeName({ entry }) {
  if (entry.isDiningOut) return "Dining out";
  const title = entry.recipeTitle || "Untitled recipe";
  if (!entry.recipeLink) return title;
  return (
    <a href={`/shared/${entry.recipeLink}`} style={{ color: COLORS.accent, fontWeight: 700 }}>
      {title}
    </a>
  );
}

export function WeekPlanPreview({ plan, colors }) {
  const { soft, accent, line } = colors;
  const mealsByDay = {};
  (plan.meals || []).forEach((m) => {
    mealsByDay[m.day] = mealsByDay[m.day] || {};
    mealsByDay[m.day][m.mealSlot] = mealsByDay[m.day][m.mealSlot] || [];
    mealsByDay[m.day][m.mealSlot].push(m);
  });
  const itemsByType = { snack: [], preserves: [], medicinal: [] };
  (plan.items || []).forEach((it) => {
    if (itemsByType[it.listType]) itemsByType[it.listType].push(it);
  });

  return (
    <div>
      <h2 style={{ fontSize: FONT_SIZE.h3, fontWeight: 700, margin: "0 0 var(--space-xs)" }}>{plan.title || `Week of ${formatWeekRange(plan.weekStart)}`}</h2>
      {plan.title && (
        <div className="mp-sans" style={{ fontSize: FONT_SIZE.base, color: soft, marginBottom: SPACE.xl2 }}>
          Week of {formatWeekRange(plan.weekStart)}
        </div>
      )}

      {DAYS.map((day) => {
        const daySlots = mealsByDay[day];
        if (!daySlots) return null;
        return (
          <div key={day} style={{ marginBottom: SPACE.xxl }}>
            <div className="mp-sans" style={{ fontSize: FONT_SIZE.md, fontWeight: 700, borderBottom: `1px solid ${line}`, marginBottom: SPACE.sm, paddingBottom: SPACE.xs, color: accent }}>
              {day}
            </div>
            {MEAL_SLOTS.map((slot) => {
              const entries = (daySlots[slot] || []).filter((m) => m.recipeId || m.isDiningOut);
              if (entries.length === 0) return null;
              return (
                <div key={slot} className="mp-sans" style={{ fontSize: FONT_SIZE.base, marginBottom: SPACE.xs }}>
                  <span style={{ fontWeight: 700, textTransform: "capitalize" }}>{slot}:</span>{" "}
                  {entries.map((m, i) => (
                    <span key={i}>
                      {i > 0 && "; "}
                      {RECIPE_TYPE_LABELS[m.dishType] || m.dishType} — <RecipeName entry={m} />
                      {m.isLeftover ? " (leftovers)" : ""}
                    </span>
                  ))}
                </div>
              );
            })}
          </div>
        );
      })}

      {[
        ["snack", "Snacks/Treats"],
        ["preserves", "Preserves"],
        ["medicinal", "Medicinal"],
      ].map(
        ([type, label]) =>
          itemsByType[type].length > 0 && (
            <div key={type} style={{ marginBottom: SPACE.xl2 }}>
              <div className="mp-sans" style={{ fontSize: FONT_SIZE.md, fontWeight: 700, borderBottom: `1px solid ${line}`, marginBottom: SPACE.sm, paddingBottom: SPACE.xs, color: accent }}>
                {label}
              </div>
              {itemsByType[type].map((it, i) => (
                <div key={i} className="mp-sans" style={{ fontSize: FONT_SIZE.base, marginBottom: SPACE.xs }}>
                  <RecipeName entry={it} />
                </div>
              ))}
            </div>
          )
      )}
    </div>
  );
}

export function WeekPlanDayPreview({ day, colors }) {
  const { soft } = colors;
  const mealsBySlot = {};
  (day.meals || []).forEach((m) => {
    mealsBySlot[m.mealSlot] = mealsBySlot[m.mealSlot] || [];
    mealsBySlot[m.mealSlot].push(m);
  });

  return (
    <div>
      <h2 style={{ fontSize: FONT_SIZE.h3, fontWeight: 700, margin: "0 0 var(--space-xl2)" }}>{day.day}</h2>
      {MEAL_SLOTS.map((slot) => {
        const entries = (mealsBySlot[slot] || []).filter((m) => m.recipeId || m.isDiningOut);
        if (entries.length === 0) return null;
        return (
          <div key={slot} style={{ marginBottom: SPACE.xl }}>
            <div className="mp-sans" style={{ fontSize: FONT_SIZE.sm, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: colors.accent, marginBottom: SPACE.xs }}>
              {slot}
            </div>
            {entries.map((m, i) => (
              <div key={i} className="mp-sans" style={{ fontSize: FONT_SIZE.md, marginBottom: SPACE.hair }}>
                <span style={{ color: soft }}>{RECIPE_TYPE_LABELS[m.dishType] || m.dishType}:</span> <RecipeName entry={m} />
                {m.isLeftover ? " (leftovers)" : ""}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}
